import { NULLVALUES } from '../constants/constants.js';

/**
 * Replaces the backend's "unset int" sentinel with the wire-safe equivalent, in place and recursively.
 *
 * The C# backend uses int.MinValue (NULLVALUES.INT) to mean "not set". That value comes down in API
 * responses, gets bound into forms, and is then echoed back when the object is posted - and our WAF
 * blocks any request containing a 32-bit integer extreme. Normalising a payload as it arrives means the
 * value never lives in the client at all, so it cannot be sent back, and "not set" dropdown options can
 * use NULLVALUES.INT_WIRE as their value.
 *
 * The API maps NULLVALUES.INT_WIRE back to its own sentinel - see TTT.BLL.Constants.NullIntWire and
 * TTT.BLL.Tools.Helper.FromWireInt.
 *
 * Only int.MinValue is translated. int.MaxValue is not a backend null sentinel, so rewriting it could
 * discard a legitimate value.
 *
 * Deliberately avoids `typeof` - Babel compiles it to an ESM-only helper that Jest cannot load here.
 *
 * @param {*} value object, array or primitive to normalise
 * @returns {*} the same reference, normalised
 */
const toWireSafeInts = (value) => {
  if (Array.isArray(value)) {
    value.forEach((item, i) => {
      value[i] = toWireSafeInts(item);
    });
    return value;
  }

  if (value instanceof Object) {
    Object.keys(value).forEach((key) => {
      value[key] = toWireSafeInts(value[key]);
    });
    return value;
  }

  return value === NULLVALUES.INT ? NULLVALUES.INT_WIRE : value;
};

export { toWireSafeInts };
